import { useSearchParams } from 'next/navigation'
import pluralize from 'pluralize'
import * as React from 'react'

// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-responsive-images
import { ProgressiveAsset, Star } from '@syconium/little-miss-figgy'

import {
  ProductCatalogProduct,
  ProductCatalogVariant,
  ProductCatalogVariantGroup,
} from '../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../lib/analytics'
import { useProductReviews } from '../../../lib/hooks/useProductReviews'
import { useProductReviewsHref } from '../../../lib/routing'

import {
  Container,
  Layout,
  ProductDetailsCopy,
  ReviewCountTextContainer,
  ReviewCountTextHeader,
  ReviewStarContainerHeader,
  ReviewStarIconContainerHeader,
  ReviewStars,
  Title,
  TitleContainer,
  TitleSection,
} from './styles'

export interface IProps {
  product: {
    handle: ProductCatalogProduct['handle']
  }
  variantGroup: {
    title: string
    color: Pick<NonNullable<ProductCatalogVariantGroup['color']>, 'id'> | undefined | null
    variant: {
      shopifyProductId: ProductCatalogVariant['shopifyProductId']
    }
  }
}

export const ReviewSection: React.FC<IProps> = ({ product, variantGroup }) => {
  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
  const { detailsSection: detailsSectionCopy } = useTranslation()
  const { productReviews, productReviewsStatus } = useProductReviews({
    shopifyProductId: variantGroup.variant.shopifyProductId,
  })

  const reviewsViewPath = useProductReviewsHref({
    handle: product.handle,
    colorKey: variantGroup.color?.id,
    query: {
      plain: plainQueryParamValue,
    },
  })

  const reviewTitleText: string =
    typeof productReviews?.totalReviews === 'number'
      ? `${productReviews.totalReviews.toLocaleString()} ${pluralize(
          detailsSectionCopy.reviewsCountNoun,
          productReviews.totalReviews
        )}`
      : detailsSectionCopy.writeReviewCTA

  const displayRating = (productReviews?.averageScore || 0).toFixed(2)

  const tooltip = `Rating of this product is ${displayRating} out of 5.`

  const displaySkeletonUI = productReviewsStatus === 'pending' || productReviewsStatus === 'idle'

  return (
    <Layout>
      <Container>
        <TitleContainer>
          <TitleSection>
            <Title
              asTag='h1'
              {...trackEvent({
                category: 'product details',
                action: 'snapshot',
                label: 'title',
                value: variantGroup.title,
              })}
            >
              {variantGroup.title}
            </Title>
          </TitleSection>
        </TitleContainer>
        <ReviewStarContainerHeader data-testid='product-reviews' href={reviewsViewPath}>
          <ProgressiveAsset.Skeleton isVisible={displaySkeletonUI} />
          <ReviewStarIconContainerHeader isVisible={!displaySkeletonUI}>
            {productReviews?.averageScore === 0 || productReviews?.averageScore === null ? (
              Array.from({ length: 5 }, (_item, index: number) => (
                <Star key={index} height={16} width={16} />
              ))
            ) : (
              <ReviewStars
                rating={productReviews?.averageScore || 0}
                aria-label={tooltip}
                title={tooltip}
                isLarge
              />
            )}
          </ReviewStarIconContainerHeader>
          <ReviewCountTextContainer isVisible={!displaySkeletonUI}>
            <ProductDetailsCopy>
              <ReviewCountTextHeader>
                {' '}
                {productReviews?.averageScore === 0 || productReviews?.averageScore === null ? (
                  <u>{reviewTitleText}</u>
                ) : (
                  <span>
                    (<u>{reviewTitleText}</u>)
                  </span>
                )}
              </ReviewCountTextHeader>
            </ProductDetailsCopy>
          </ReviewCountTextContainer>
        </ReviewStarContainerHeader>
      </Container>
    </Layout>
  )
}
